import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'components/Form/Dropdown';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { classNames } from 'support/helpers/generic/generic';
import { useMemo } from 'react';

type DropdownValue = {
  label: string;
  value: string;
};

type DropdownFilterProps = {
  label?: string;
  filterKey: string;
  dropdownValues: Array<DropdownValue>;
  showAllLabel?: string;
  defaultToFirstValue?: boolean;
  className?: string;
  testId?: string;
  currentValue: string;
  onChange: (filterKey: string, newValue: string) => void;
  hideValueFromButton?: boolean;
  multiple?: boolean;
};

export const DropdownFilter: FC<DropdownFilterProps> = ({
  label,
  filterKey,
  currentValue,
  dropdownValues,
  showAllLabel,
  defaultToFirstValue,
  onChange,
  className,
  testId,
  hideValueFromButton,
  multiple,
}) => {
  const { t } = useTranslation();
  const defaultValue = useMemo(
    () => ({
      value: '_procuros_all',
      label: showAllLabel ?? t('common:allValues'),
    }),
    [showAllLabel, t],
  );

  const translatedDropdownValues = useMemo(
    () =>
      dropdownValues.map((dropdownValue: DropdownValue) => ({
        label: dropdownValue.label,
        value: dropdownValue.value,
      })),
    [dropdownValues],
  );

  const options = useMemo(() => {
    if (defaultToFirstValue) {
      return translatedDropdownValues;
    }
    return [defaultValue, ...translatedDropdownValues];
  }, [defaultToFirstValue, defaultValue, translatedDropdownValues]);

  const value = useMemo(() => {
    const baseValue = currentValue || (defaultToFirstValue ? options[0]?.value : defaultValue.value);
    return baseValue;
  }, [currentValue, defaultToFirstValue, options, defaultValue.value]);

  // Create a reusable icon component
  const filterIcon = <DropdownFilterIcon currentValue={currentValue} />;

  // Common properties for both single and multiple mode
  const commonProps = {
    className,
    dropdownDisplayTestId: testId,
    options,
    emptySelectionLabel: label,
    showSelection: !hideValueFromButton,
    leftIcon: filterIcon,
  };
  const handleChange = (newValue: string | Array<string>) => {
    const selectedValue = Array.isArray(newValue) ? newValue : [newValue];
    const isAllSelected = selectedValue.at(-1) === defaultValue.value;
    const filteredValue = selectedValue.filter((v) => v !== defaultValue.value);
    const finalValue = isAllSelected ? '' : Array.isArray(newValue) ? filteredValue.join(',') : newValue;

    onChange(filterKey, finalValue);
  };

  // Cast to generic component with the correct type
  const DropdownComponent = Dropdown as typeof Dropdown<string>;

  //Typescript issues, that's why the check by multiple
  return multiple ? (
    <DropdownComponent
      {...commonProps}
      value={value.split(',')}
      onChange={handleChange}
      multiple={true}
      searchEnabled
    />
  ) : (
    <DropdownComponent {...commonProps} value={value} onChange={handleChange} multiple={multiple} searchEnabled />
  );
};

type DropdownFilterIconProps = {
  currentValue: string;
  className?: string;
};
const DropdownFilterIcon: FC<DropdownFilterIconProps> = ({ currentValue, className }) =>
  currentValue ? (
    <span
      className={classNames(
        'flex items-center justify-center h-5 w-5 border border-procuros-green-500 bg-procuros-green-50 text-procuros-green-500 rounded-full',
        className,
      )}
    >
      {currentValue.split(',').length}
    </span>
  ) : (
    <FunnelIcon className={className} />
  );
